import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import itLocale from "@fullcalendar/core/locales/it";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MultiSelect } from "@/components/MultiSelect";
import { AttivitaTable } from "@/components/AttivitaTable";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PlusIcon as Plus, X, WarningIcon as Warning, MagnifyingGlassIcon as MagnifyingGlass, SpinnerIcon as Spinner, WrenchIcon as Wrench } from "@phosphor-icons/react";
import "@/fullcalendar.css";

const VIEW_MAP = { settimana: "timeGridWeek", week: "timeGridWeek", mese: "dayGridMonth", month: "dayGridMonth", giorno: "timeGridDay", day: "timeGridDay", agenda: "listWeek" };
const stripHtml = (h) => (h || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
const validSess = (s) => s && s.data && s.ora_inizio && s.ora_fine && s.ora_fine > s.ora_inizio;

export default function Attivita() {
  const navigate = useNavigate();
  const calRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [initialView, setInitialView] = useState(null);
  const [fStati, setFStati] = useState([]);
  const [fModalita, setFModalita] = useState([]);
  const [fTecnici, setFTecnici] = useState([]);
  const [fZone, setFZone] = useState([]);
  const [overlapDlg, setOverlapDlg] = useState(null); // { items, onConfirm, onCancel }
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("attivita_view_mode") || "calendar"); // 'calendar' | 'attivita'
  const [calView, setCalView] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDone, setSearchDone] = useState(false);

  useEffect(() => { localStorage.setItem("attivita_view_mode", viewMode); }, [viewMode]);

  useEffect(() => {
    const q = searchQ.trim();
    if (!searchOpen) return;
    if (q.length < 2) { setSearchResults([]); setSearchDone(false); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(() => {
      api.get(`/attivita/search?q=${encodeURIComponent(q)}`)
        .then(({ data }) => { setSearchResults(data || []); setSearchDone(true); })
        .catch(() => { setSearchResults([]); setSearchDone(true); })
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen]);

  const openResult = (id) => { setSearchOpen(false); navigate(`/interventi/${id}`); };

  const load = useCallback(() => {
    api.get("/attivita").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/tecnici").then(({ data }) => setTecnici([...data].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
    api.get("/interventi-config").then(({ data }) => {
      setCfg(data);
      setInitialView(VIEW_MAP[data?.calendario?.vista_default] || "timeGridWeek");
    }).catch(() => setInitialView("timeGridWeek"));
    load();
  }, [load]);

  useEffect(() => { if (initialView && !calView) setCalView(initialView); }, [initialView, calView]);

  const switchCalView = (v) => {
    setCalView(v);
    setViewMode("calendar");
    setTimeout(() => calRef.current?.getApi()?.changeView(v), 0);
  };

  const statiCfg = useMemo(() => cfg?.stati || [], [cfg]);
  const modalitaCfg = useMemo(() => (cfg?.modalita || []).filter((m) => m.attivo !== false), [cfg]);
  const zoneCfg = useMemo(() => (cfg?.zone || []).filter((z) => z.attivo !== false), [cfg]);
  const statoColor = useCallback((k) => (statiCfg.find((s) => s.key === k) || {}).color || "#64748b", [statiCfg]);
  const statoLabel = useCallback((k) => (statiCfg.find((s) => s.key === k) || {}).label || k, [statiCfg]);
  const modalitaLabel = useCallback((k) => (modalitaCfg.find((m) => m.key === k) || {}).label || k || "—", [modalitaCfg]);
  const tecName = useCallback((tid) => (tecnici.find((t) => t.id === tid) || {}).name, [tecnici]);

  const passFilters = useCallback((a) => {
    if (fStati.length && !fStati.includes(a.stato)) return false;
    if (fModalita.length && !fModalita.includes(a.modalita_addebito)) return false;
    if (fZone.length && !fZone.includes(a.zona)) return false;
    if (fTecnici.length) {
      const ids = [...(a.tecnici_ids || []), ...(a.sessioni_lavoro || []).map((s) => s.tecnico_id)];
      if (!fTecnici.some((t) => ids.includes(t))) return false;
    }
    return true;
  }, [fStati, fModalita, fZone, fTecnici]);
  const hasFilters = !!(fStati.length || fModalita.length || fTecnici.length || fZone.length);
  const resetFilters = () => { setFStati([]); setFModalita([]); setFTecnici([]); setFZone([]); };

  // Eventi: una sessione valida = uno o due eventi (spezzata dalla pausa)
  const events = useMemo(() => {
    const out = [];
    for (const a of items) {
      if (!passFilters(a)) continue;
      const frozen = a.stato === "completata";
      for (const s of (a.sessioni_lavoro || [])) {
        if (!validSess(s)) continue;
        if (fTecnici.length && !fTecnici.includes(s.tecnico_id)) continue;
        const color = statoColor(a.stato);
        const base = {
          backgroundColor: color, borderColor: color,
          editable: !frozen, startEditable: !frozen, durationEditable: !frozen,
        };
        const common = { att_id: a.id, session_id: s.session_id, cliente: a.cliente_nome, tecnico_id: s.tecnico_id, numero: a.numero, stato: a.stato };
        const pi = s.pausa_inizio, pf = s.pausa_fine;
        const hasPause = pi && pf && pi > s.ora_inizio && pf < s.ora_fine && pf > pi;
        if (hasPause) {
          out.push({ id: `${a.id}__${s.session_id || Math.random()}__b`, start: `${s.data}T${s.ora_inizio}`, end: `${s.data}T${pi}`, ...base,
            extendedProps: { ...common, part: "before" } });
          out.push({ id: `${a.id}__${s.session_id || Math.random()}__a`, start: `${s.data}T${pf}`, end: `${s.data}T${s.ora_fine}`, ...base,
            extendedProps: { ...common, part: "after" } });
        } else {
          out.push({ id: `${a.id}__${s.session_id || Math.random()}`, start: `${s.data}T${s.ora_inizio}`, end: `${s.data}T${s.ora_fine}`, ...base,
            extendedProps: { ...common, part: "full" } });
        }
      }
    }
    return out;
  }, [items, passFilters, fTecnici, statoColor]);

  // Da pianificare: nessuna sessione valida
  const daPianificare = useMemo(() => items.filter((a) => ["da_pianificare", "in_attesa_conferma"].includes(a.stato) && passFilters(a)), [items, passFilters]);

  const renderEvent = (arg) => {
    const p = arg.event.extendedProps;
    const tec = p.tecnico_id ? (tecName(p.tecnico_id) || "Tecnico") : "Tecnico non assegnato";
    const time = arg.timeText;
    return (
      <div className="fc-ev-body" data-testid="cal-event">
        <div className="fc-ev-client">{p.cliente || "Cliente non selezionato"}</div>
        <div className="fc-ev-tec">{tec}{time ? ` · ${time}` : ""}</div>
        <div className="fc-ev-meta">{p.numero ? `n. ${p.numero}` : ""}{p.stato ? ` · ${statoLabel(p.stato)}` : ""}</div>
      </div>
    );
  };

  const onEventClick = (info) => { navigate(`/interventi/${info.event.extendedProps.att_id}`); };

  const onSelect = (info) => {
    const start = info.start;
    const pad = (n) => String(n).padStart(2, "0");
    const data = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    let inizio, fine;
    if (info.allDay) {
      inizio = cfg?.calendario?.ora_inizio || "09:00";
      const slot = cfg?.calendario?.slot_minuti || 60;
      const [h, m] = inizio.split(":").map(Number);
      const endM = h * 60 + m + slot;
      fine = `${pad(Math.floor(endM / 60))}:${pad(endM % 60)}`;
    } else {
      inizio = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
      const end = info.end || new Date(start.getTime() + (cfg?.calendario?.slot_minuti || 60) * 60000);
      fine = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
    }
    navigate(`/interventi/nuovo?data=${data}&inizio=${inizio}&fine=${fine}`);
    calRef.current?.getApi()?.unselect();
  };

  const checkOverlap = async (params) => {
    try { const { data } = await api.post("/attivita/check-overlap", params); return data; }
    catch { return { overlaps: false, items: [] }; }
  };
  const askOverlap = (items) => new Promise((resolve) => setOverlapDlg({ items, resolve }));

  const patchSession = async (info) => {
    const p = info.event.extendedProps;
    if (!p.session_id) { info.revert(); toast.error("Sessione non identificabile, riprova dopo un ricaricamento"); return; }
    const s = info.event.start, e = info.event.end;
    const pad = (n) => String(n).padStart(2, "0");
    const dateStr = `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`;
    const startHHMM = `${pad(s.getHours())}:${pad(s.getMinutes())}`;
    const endHHMM = e ? `${pad(e.getHours())}:${pad(e.getMinutes())}` : undefined;
    // Blocchi spezzati dalla pausa: il bordo interno modifica la pausa
    const body = { data: dateStr };
    if (p.part === "before") {
      body.ora_inizio = startHHMM;
      if (endHHMM) body.pausa_inizio = endHHMM;
    } else if (p.part === "after") {
      body.pausa_fine = startHHMM;
      if (endHHMM) body.ora_fine = endHHMM;
    } else {
      body.ora_inizio = startHHMM;
      if (endHHMM) body.ora_fine = endHHMM;
    }
    // Overlap check solo quando si sposta/ridimensiona la sessione intera (non i bordi della pausa)
    if (p.tecnico_id && p.part === "full" && body.ora_fine) {
      const ov = await checkOverlap({ tecnico_id: p.tecnico_id, data: body.data, ora_inizio: body.ora_inizio, ora_fine: body.ora_fine, exclude_attivita_id: p.att_id, exclude_session_id: p.session_id });
      if (ov.overlaps) {
        const ok = await askOverlap(ov.items);
        if (!ok) { info.revert(); return; }
      }
    }
    try {
      await api.patch(`/attivita/${p.att_id}/sessioni/${p.session_id}`, body);
      toast.success("Sessione aggiornata");
      load();
    } catch (err) { info.revert(); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cal = cfg?.calendario || {};

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10">
        <div className="flex items-start justify-between gap-4 mb-5">
          <PageHeader title="Interventi" subtitle="Calendario tecnici · ogni evento è una sessione di lavoro" />
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => { setSearchOpen(true); setSearchQ(""); setSearchResults([]); setSearchDone(false); }} className="rounded-sm gap-2" data-testid="search-attivita-btn"><MagnifyingGlass size={16} weight="bold" /> Cerca intervento</Button>
            <Button onClick={() => navigate("/interventi/nuovo")} className="rounded-sm gap-2" data-testid="new-attivita-btn"><Plus size={16} weight="bold" /> Nuovo intervento</Button>
          </div>
        </div>

        {/* Selettore viste: Mese / Settimana / Giorno / Agenda / Attività */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="view-switcher">
            {[
              { key: "dayGridMonth", label: "Mese" },
              { key: "timeGridWeek", label: "Settimana" },
              { key: "timeGridDay", label: "Giorno" },
              { key: "listWeek", label: "Agenda" },
            ].map((v) => {
              const active = viewMode === "calendar" && calView === v.key;
              return (
                <button key={v.key} type="button" onClick={() => switchCalView(v.key)} data-testid={`view-${v.key}`}
                  className={`px-3 py-1.5 text-sm border-r border-border last:border-r-0 transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/60"}`}>
                  {v.label}
                </button>
              );
            })}
            <button type="button" onClick={() => setViewMode("attivita")} data-testid="view-attivita"
              className={`px-3 py-1.5 text-sm transition-colors ${viewMode === "attivita" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/60"}`}>
              Elenco
            </button>
          </div>
        </div>

        {/* Filtri multi-selezione (solo vista calendario) */}
        {viewMode === "calendar" && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="filters-bar">
          <MultiSelect testid="filter-tecnico" width="w-48" placeholder="Tutti i tecnici"
            options={tecnici.map((t) => ({ value: t.id, label: t.name }))} value={fTecnici} onChange={setFTecnici} />
          <MultiSelect testid="filter-stato" width="w-48" placeholder="Tutti gli stati"
            options={statiCfg.map((s) => ({ value: s.key, label: s.label, color: s.color }))} value={fStati} onChange={setFStati} />
          <MultiSelect testid="filter-modalita" width="w-52" placeholder="Tutte le modalità"
            options={modalitaCfg.map((m) => ({ value: m.key, label: m.label }))} value={fModalita} onChange={setFModalita} />
          <MultiSelect testid="filter-zona" width="w-44" placeholder="Tutte le zone"
            options={zoneCfg.map((z) => ({ value: z.label, label: z.label }))} value={fZone} onChange={setFZone} />
          {hasFilters ? (
            <Button variant="ghost" size="sm" className="rounded-sm gap-1 text-muted-foreground" onClick={resetFilters} data-testid="reset-filters-btn"><X size={14} /> Reset filtri</Button>
          ) : null}
        </div>
        )}

        <Card className={`rounded-sm border-border shadow-none p-4 ${viewMode === "attivita" ? "hidden" : ""}`} data-testid="calendar-card">
          {initialView && (
            <FullCalendar
              ref={calRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              initialView={initialView}
              locale={itLocale}
              headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
              buttonText={{ today: "Oggi", month: "Mese", week: "Settimana", day: "Giorno", list: "Agenda" }}
              events={events}
              eventContent={renderEvent}
              eventDisplay="block"
              editable
              eventResizableFromStart
              selectable
              selectMirror
              select={onSelect}
              eventClick={onEventClick}
              eventDrop={patchSession}
              eventResize={patchSession}
              slotDuration={`00:${String(cal.slot_minuti || 30).padStart(2, "0")}:00`}
              slotLabelInterval="01:00:00"
              slotLabelFormat={{ hour: "numeric", minute: "2-digit", omitZeroMinute: true, hour12: false }}
              slotMinTime={`${(cal.ora_inizio || "08:00")}:00`}
              slotMaxTime={`${(cal.ora_fine || "18:00")}:00`}
              allDaySlot={false}
              nowIndicator
              height="auto"
              firstDay={1}
            />
          )}
        </Card>

        {viewMode === "attivita" && (
          <AttivitaTable items={items} tecnici={tecnici} statiCfg={statiCfg} modalitaCfg={modalitaCfg}
            statoColor={statoColor} statoLabel={statoLabel} modalitaLabel={modalitaLabel}
            onRowClick={(id) => navigate(`/interventi/${id}`)} onBulkDone={load} />
        )}

        {/* Da pianificare (solo vista calendario) */}
        {viewMode === "calendar" && (
        <Card className="rounded-sm border-amber-300 bg-amber-50/40 shadow-none p-5 mt-6" data-testid="da-pianificare-section">
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-amber-800 mb-3">Da pianificare ({daPianificare.length})</h2>
          {daPianificare.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun intervento da pianificare.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {daPianificare.map((a) => (
                <button key={a.id} data-testid="da-pianificare-card" onClick={() => navigate(`/interventi/${a.id}`)}
                  className="text-left rounded-sm border border-amber-200 bg-white p-3 hover:border-amber-400 transition-colors">
                  <div className="flex items-center gap-2">
                    {a.numero && <span className="font-mono text-[11px] px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground">n. {a.numero}</span>}
                    <span className="text-[11px] text-muted-foreground truncate">{modalitaLabel(a.modalita_addebito)}</span>
                    {a.lavorazione_id && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 border border-blue-200 inline-flex items-center gap-1" data-testid="da-lavorazione-badge" title={`Da lavorazione ${a.lavorazione_numero || ""}`}><Wrench size={10} /> da lavorazione</span>}
                  </div>
                  <div className="font-medium text-sm mt-1 truncate">{a.cliente_nome || "Cliente non selezionato"}</div>
                  <div className="text-xs text-muted-foreground truncate">{stripHtml(a.richiesta_intervento) || "—"}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${statoColor(a.stato)}22`, color: statoColor(a.stato) }}>{statoLabel(a.stato)}</span>
                    {a.tecnico_nome && <span className="text-[11px] text-muted-foreground">{a.tecnico_nome}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
        )}
      </div>

      <AlertDialog open={!!overlapDlg} onOpenChange={(o) => { if (!o && overlapDlg) { overlapDlg.resolve(false); setOverlapDlg(null); } }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Warning size={20} className="text-amber-500" /> Sovrapposizione rilevata</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-1.5">
                <p>La sessione si sovrappone ad altri interventi per lo stesso tecnico:</p>
                <ul className="space-y-1">
                  {(overlapDlg?.items || []).map((it, i) => (
                    <li key={i} className="rounded-sm border border-amber-200 bg-amber-50/50 px-2 py-1 text-foreground" data-testid={`overlap-item-${i}`}>
                      <b>n. {it.numero || "—"}</b> · {it.cliente_nome || "Cliente"} · {tecName(it.tecnico_id) || "Tecnico"} · {it.ora_inizio}–{it.ora_fine} ({statoLabel(it.stato)})
                    </li>
                  ))}
                </ul>
                <p>Vuoi continuare comunque?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="overlap-cancel-btn" onClick={() => { overlapDlg?.resolve(false); setOverlapDlg(null); }}>Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm" data-testid="overlap-confirm-btn" onClick={() => { overlapDlg?.resolve(true); setOverlapDlg(null); }}>Continua comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="search-attivita-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><MagnifyingGlass size={20} /> Cerca intervento</DialogTitle>
            <DialogDescription>Trova un intervento per numero rapportino, richiesta, descrizione o cliente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="rounded-sm pl-9"
                placeholder="Cerca per numero rapportino, richiesta o descrizione…" data-testid="search-attivita-input" />
            </div>
            <div className="max-h-[52vh] overflow-y-auto -mx-1 px-1" data-testid="search-attivita-results">
              {searchLoading && (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground" data-testid="search-loading">
                  <Spinner size={18} className="animate-spin" /> Ricerca in corso…
                </div>
              )}
              {!searchLoading && searchQ.trim().length >= 2 && searchDone && searchResults.length === 0 && (
                <div className="py-8 text-center text-sm text-muted-foreground" data-testid="search-empty">Nessun intervento trovato per “{searchQ.trim()}”.</div>
              )}
              {!searchLoading && searchQ.trim().length < 2 && (
                <div className="py-8 text-center text-sm text-muted-foreground">Digita almeno 2 caratteri per cercare.</div>
              )}
              {!searchLoading && searchResults.map((r) => {
                const preview = r.richiesta_preview || r.descrizione_preview || "";
                return (
                  <button key={r.id} type="button" onClick={() => openResult(r.id)} data-testid={`search-result-${r.id}`}
                    className="w-full text-left rounded-sm border border-border hover:border-primary hover:bg-secondary/40 transition-colors px-3 py-2.5 mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm">n. {r.numero || "—"}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded-sm shrink-0" style={{ backgroundColor: `${statoColor(r.stato)}22`, color: statoColor(r.stato) }}>{statoLabel(r.stato)}</span>
                    </div>
                    <div className="text-sm text-foreground mt-0.5">{r.cliente_nome || "Cliente non assegnato"}{r.data ? <span className="text-muted-foreground"> · {r.data.split("-").reverse().join("/")}</span> : null}</div>
                    {preview && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{preview}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
